<template>
  <div class="freight-table-supplier-section">
    <label class="freight-table-supplier-section__label">
      <span class="freight-table-supplier-section__required">*</span>
      Transportadora
    </label>
    <a-select
      mode="multiple"
      show-search
      allow-clear
      label-in-value
      size="large"
      option-label-prop="label"
      dropdown-class-name="freight-table-supplier-section__dropdown"
      :value="selectValue"
      :filter-option="false"
      :not-found-content="fetching ? undefined : null"
      placeholder="Informe o CNPJ da transportadora"
      class="freight-table-supplier-section__select"
      @search="onSearch"
      @change="onChange"
    >
      <a-spin v-if="fetching" slot="notFoundContent" size="small" />
      <a-select-option
        v-for="option in dropdownOptions"
        :key="option.value"
        :value="option.value"
        :label="option.label"
        :disabled="isExcluded(option.value)"
      >
        <div class="freight-table-supplier-section__option">
          <div class="freight-table-supplier-section__option-text">
            <strong>{{ option.label }}</strong>
            <span>{{ formatCnpjDisplay(option.cnpj) }}</span>
            <span
              v-if="isExcluded(option.value)"
              class="freight-table-supplier-section__linked"
            >
              Já vinculada à {{ linkedTableLabel }}
            </span>
          </div>
          <a-icon
            type="check"
            class="freight-table-supplier-section__option-check"
          />
        </div>
      </a-select-option>
    </a-select>
    <p
      v-if="emptyMessage && !fetching"
      class="freight-table-supplier-section__empty"
    >
      {{ emptyMessage }}
    </p>
  </div>
</template>

<script>
import debounce from "lodash/debounce";
import FreightTablesApi from "@/modules/FreightTables/services/FreightTablesApi";
import { formatCNPJ, formatCNPJFromParts } from "@/utils";
import { handleApiError } from "@/shared/utils/handleApiError";

const MIN_SEARCH_LENGTH = 3;

const NOT_FOUND_MESSAGE =
  "Nenhuma transportadora encontrada para a busca informada.";

function resolvePessoaId(item) {
  if (item.pessoa && item.pessoa.pessoa_id != null) {
    return item.pessoa.pessoa_id;
  }
  return item.pessoa_id;
}

// Razão social primeiro: é o nome que o legado e os detalhes da tabela mostram.
function resolveLabel(item) {
  return item.razao_social || item.nome_fantasia || item.nome || "";
}

function resolveCnpj(item) {
  if (item.cnpj) {
    return item.cnpj;
  }
  if (item.cnpj_base != null) {
    return formatCNPJFromParts(item.cnpj_base, item.cnpj_filial, item.cnpj_digito);
  }
  return item.cpf || "";
}

function mapSupplierOption(item) {
  const value = resolvePessoaId(item);
  if (value == null) return null;
  return {
    value,
    label: resolveLabel(item),
    cnpj: resolveCnpj(item),
  };
}

function collectSupplierItems(payload) {
  const data = payload || {};
  return [data.items || [], data.itemsF || [], data.itemsE || []].flat();
}

function sameId(a, b) {
  return String(a) === String(b);
}

function readOptionLabel(label) {
  if (label == null) return "";
  let text = "";
  if (typeof label === "string" || typeof label === "number") {
    text = String(label);
  } else if (label.text) {
    text = String(label.text);
  }
  // label-in-value pode devolver "nome\ncnpj" da tag; nome é só a 1ª linha.
  return text.split("\n")[0];
}

function resolveSupplierId(supplier) {
  if (supplier == null) return null;
  if (typeof supplier === "object") return supplier.pessoa_id;
  return supplier;
}

function toSelectedOption(supplier) {
  return {
    value: supplier.pessoa_id,
    label: supplier.nome_fantasia || "",
    cnpj: supplier.cnpj || "",
  };
}

function toSupplierPayload(option) {
  return {
    pessoa_id: option.value,
    nome_fantasia: option.label || "",
    cnpj: option.cnpj || "",
  };
}

function buildChoiceLabel(nome, cnpj) {
  const name = nome || "";
  const doc = cnpj ? formatCNPJ(cnpj) : "";
  return doc ? `${name}\n${doc}` : name;
}

export default {
  name: "FreightTableSupplierSection",
  props: {
    // Array de { pessoa_id, nome_fantasia, cnpj } (.sync com o formulário pai).
    value: {
      type: Array,
      default: () => [],
    },
    // pessoa_id já gravados na tabela (ativos e inativos). Aparecem desabilitados.
    excludeIds: {
      type: Array,
      default: () => [],
    },
    linkedTableLabel: {
      type: String,
      default: "tabela de frete",
    },
  },
  data() {
    return {
      fetching: false,
      options: [],
      selectedOptions: [],
      emptyMessage: "",
      requestSeq: 0,
    };
  },
  computed: {
    selectedIds() {
      return (this.value || []).map(resolveSupplierId).filter((id) => id != null);
    },
    selectValue() {
      return this.selectedIds.map((id) => {
        const fromValue = (this.value || []).find((supplier) =>
          sameId(resolveSupplierId(supplier), id)
        );
        const found = this.selectedOptions.find((option) =>
          sameId(option.value, id)
        );
        const nome =
          (fromValue && fromValue.nome_fantasia) ||
          (found && found.label) ||
          String(id);
        const cnpj =
          (fromValue && fromValue.cnpj) || (found && found.cnpj) || "";
        return {
          key: id,
          label: buildChoiceLabel(nome, cnpj),
        };
      });
    },
    dropdownOptions() {
      return this.options.filter(
        (option) =>
          !this.selectedIds.some((id) => sameId(id, option.value))
      );
    },
  },
  watch: {
    value: {
      immediate: true,
      handler(list) {
        const suppliers = Array.isArray(list) ? list : [];
        this.selectedOptions = suppliers
          .filter((supplier) => resolveSupplierId(supplier) != null)
          .map((supplier) =>
            typeof supplier === "object"
              ? toSelectedOption(supplier)
              : { value: supplier, label: String(supplier), cnpj: "" }
          );
      },
    },
  },
  created() {
    this.debouncedFetch = debounce(this.fetchSuppliers, 500);
  },
  beforeDestroy() {
    if (this.debouncedFetch) {
      this.debouncedFetch.cancel();
    }
  },
  methods: {
    // Máscara pt-BR (XX.XXX.XXX/XXXX-XX) apenas para exibição no dropdown.
    formatCnpjDisplay(cnpj) {
      return formatCNPJ(cnpj);
    },
    isExcluded(id) {
      return (this.excludeIds || []).some((excluded) => sameId(excluded, id));
    },
    onSearch(term) {
      this.emptyMessage = "";
      this.debouncedFetch(term);
    },
    onChange(selected) {
      const items = Array.isArray(selected) ? selected : [];
      const allowed = items.filter((item) => !this.isExcluded(item.key));
      this.selectedOptions = allowed.map((item) => {
        const prev = this.selectedOptions.find((option) =>
          sameId(option.value, item.key)
        );
        const fromSearch = this.options.find((option) =>
          sameId(option.value, item.key)
        );
        return {
          value: item.key,
          label:
            (fromSearch && fromSearch.label) ||
            (prev && prev.label) ||
            readOptionLabel(item.label),
          cnpj:
            (fromSearch && fromSearch.cnpj) || (prev && prev.cnpj) || "",
        };
      });
      this.options = [];
      this.fetching = false;
      this.emptyMessage = "";
      this.$emit(
        "update:value",
        this.selectedOptions.map(toSupplierPayload)
      );
    },
    fetchSuppliers(term) {
      const search = String(term || "").trim();
      if (search.length < MIN_SEARCH_LENGTH) {
        this.options = [];
        this.fetching = false;
        this.emptyMessage = "";
        return;
      }

      const seq = ++this.requestSeq;
      this.fetching = true;
      this.emptyMessage = "";

      FreightTablesApi.listSuppliers(search)
        .then((response) => {
          if (seq !== this.requestSeq) return;
          const payload = (response && response.data) || {};
          this.options = collectSupplierItems(payload)
            .map(mapSupplierOption)
            .filter(Boolean);
          this.emptyMessage = this.dropdownOptions.length
            ? ""
            : NOT_FOUND_MESSAGE;
        })
        .catch((error) => {
          if (seq !== this.requestSeq) return;
          this.options = [];
          this.emptyMessage = "";
          handleApiError(
            this,
            error,
            "Não foi possível buscar as transportadoras"
          );
        })
        .then(() => {
          if (seq !== this.requestSeq) return;
          this.fetching = false;
        });
    },
  },
};
</script>

<style scoped lang="less">
@import "~@/shared/styles/variables.less";

.freight-table-supplier-section {
  font-family: @font-primary;

  &__label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    line-height: 22px;
    color: rgba(0, 0, 0, 0.88);
  }

  &__required {
    color: #ff4d4f;
  }

  &__select {
    width: 100%;
  }

  &__empty {
    margin: 8px 0 0;
    font-size: 12px;
    line-height: 20px;
    color: rgba(0, 0, 0, 0.45);
  }

  // Campo — alinhado ao Figma/SVG e à IdentificationSection (raio 8px, 16/24).
  /deep/ .ant-select-selection {
    border-radius: 8px;
    min-height: 40px;
    border-color: #d9d9d9;
    padding: 4px 8px;
    box-shadow: none;
  }

  /deep/ .ant-select-focused .ant-select-selection,
  /deep/ .ant-select-selection:focus,
  /deep/ .ant-select-selection:active,
  /deep/ .ant-select-open .ant-select-selection {
    border-color: @color-primary;
    box-shadow: 0 0 0 2px fade(@color-primary, 16%);
  }

  /deep/ .ant-select-selection__placeholder {
    color: rgba(0, 0, 0, 0.25);
    font-size: 16px;
    line-height: 24px;
  }

  /deep/ .ant-select-selection__rendered {
    margin-left: 4px;
    line-height: 30px;
  }

  // Tag selecionada: nome + CNPJ em duas linhas (label com \n + pre-line).
  /deep/ .ant-select-selection--multiple .ant-select-selection__choice {
    border-radius: 6px;
    background: @color-bg-info;
    border-color: transparent;
    color: @color-text-dark;
    height: auto;
    max-width: 100%;
    margin-top: 4px;
    padding: 4px 24px 4px 8px;
    line-height: 1.3;
    white-space: normal;
  }

  /deep/ .ant-select-selection--multiple .ant-select-selection__choice__content {
    display: inline-block;
    white-space: pre-line;
    font-size: 14px;
    line-height: 18px;
    vertical-align: middle;
  }

  /deep/ .ant-select-selection--multiple .ant-select-selection__choice__remove {
    top: 50%;
    transform: translateY(-50%);
    right: 6px;
  }

  /deep/ .ant-select-search--inline .ant-select-search__field {
    font-size: 16px;
    line-height: 24px;
  }
}
</style>

<!-- Dropdown é renderizado no body: estilos sem scoped. -->
<style lang="less">
@import "~@/shared/styles/variables.less";

.freight-table-supplier-section__dropdown {
  font-family: @font-primary;
  border-radius: 8px;
  padding: 0;
  overflow: hidden;
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);

  .ant-select-dropdown-menu {
    max-height: 280px;
    padding: 0;
  }

  .ant-select-dropdown-menu-item {
    padding: 12px 16px;
    border-bottom: 1px solid #f0f0f0;
    line-height: 1.3;

    &:last-child {
      border-bottom: none;
    }

    &:hover:not(.ant-select-dropdown-menu-item-disabled) {
      background: #f5f8fb;
    }
  }

  .ant-select-dropdown-menu-item-active:not(.ant-select-dropdown-menu-item-disabled) {
    background: @color-bg-info;
  }

  .freight-table-supplier-section__option {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
  }

  .freight-table-supplier-section__option-text {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;

    strong {
      font-size: 14px;
      font-weight: 600;
      line-height: 22px;
      color: rgba(0, 0, 0, 0.88);
    }

    span {
      font-size: 12px;
      line-height: 20px;
      color: rgba(0, 0, 0, 0.45);
    }

    .freight-table-supplier-section__linked {
      color: @color-primary;
      font-weight: 600;
    }
  }

  .ant-select-dropdown-menu-item-disabled
    .freight-table-supplier-section__option-text {
    strong,
    span {
      color: rgba(0, 0, 0, 0.35);
    }

    .freight-table-supplier-section__linked {
      color: @color-primary;
    }
  }

  &.ant-select-dropdown--multiple .ant-select-dropdown-menu-item .ant-select-selected-icon {
    display: none;
  }

  .freight-table-supplier-section__option-check {
    flex: none;
    font-size: 14px;
    color: @color-primary;
    opacity: 0;
  }

  .ant-select-dropdown-menu-item-active
    .freight-table-supplier-section__option-check,
  .ant-select-dropdown-menu-item-selected
    .freight-table-supplier-section__option-check {
    opacity: 1;
  }
}
</style>
